import { useCallback } from "react";
import {
  BidStatus,
  SourcingEventStatus,
  type BuyerBidDetailDto,
  useGetSourcingEventQuery,
  useRevokeBidRejectionMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";

export type RevokeOutcome = { ok: true } | { ok: false; error: unknown };

export interface BidRejectionDecision {
  isRejected: boolean;
  canRevoke: boolean;
  revoke: () => Promise<boolean>;
  revokeOutcome: () => Promise<RevokeOutcome>;
  isLoading: boolean;
}

export const useBidRejectionDecision = (detail: BuyerBidDetailDto): BidRejectionDecision => {
  const { triggerSoft } = useApiError();
  const [revokeRejection, { isLoading }] = useRevokeBidRejectionMutation();
  const { data: event, refetch: refetchEvent } = useGetSourcingEventQuery({ eventId: detail.bid.forEvent });

  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const isRejected = detail.bid.status === BidStatus.Rejected;

  const revoke = useCallback(async () => {
    try {
      await revokeRejection({ eventId, bidId }).unwrap();
      return true;
    } catch (e) {
      triggerSoft(e);
      return false;
    }
  }, [revokeRejection, eventId, bidId, triggerSoft]);

  const revokeOutcome = useCallback(async (): Promise<RevokeOutcome> => {
    try {
      await revokeRejection({ eventId, bidId }).unwrap();
      return { ok: true };
    } catch (error) {
      refetchEvent();
      return { ok: false, error };
    }
  }, [revokeRejection, eventId, bidId, refetchEvent]);

  return {
    isRejected,
    canRevoke: isRejected && event?.status === SourcingEventStatus.Published,
    revoke,
    revokeOutcome,
    isLoading,
  };
};
